import { type LocationQueryValue } from 'vue-router';
import { notify } from "@kyvg/vue3-notification";
import dayjs from "dayjs";
import 'dayjs/locale/ko';
import relativeTime from 'dayjs/plugin/relativeTime';
import weekday from 'dayjs/plugin/weekday';
import DOMPurify from 'dompurify';
import { isArray, isString } from 'lodash-es';
import { type DisplayBreakpoint } from "vuetify";
import type { DYAppConfig } from "../app.config.value";
import type { ApiSimpleResponseStructure } from "../composables/common";
import { useDYAppConfig } from "../composables/config";
import type CloudFlareFlexibleVariants from "./CloudFlareFlexibleVariants";
import { useAppLinkHandler } from '~/modules/nuxt3-module-doyac/composables/appLinkHandler';


export const animateCSS = (element: Element, animation: string, prefix = '') =>
  // We create a Promise and return it
  new Promise((resolve, reject) => {
    const animationName = `${prefix}${animation}`

    element.classList.add(`${prefix}animated`, animationName)

    // When the animation ends, we clean the classes and resolve the Promise
    function handleAnimationEnd(event: Event) {
      event.stopPropagation()
      element.classList.remove(`${prefix}animated`, animationName)
      resolve('Animation ended')
    }

    element.addEventListener('animationend', handleAnimationEnd, { once: true })
  })

export const apiListStructureCudNotify = (response: ApiSimpleResponseStructure) => { // CUD에 해당하는 노티파이 처리
  if (response.message) { notify({ type: response.allowed ? 'success' : 'error', text: response.message }) }
}

export const appLink = (url: string, fromDynamicLink: string) => {
  const { ANDROID_APP_PACKAGE_NAME } = useDYAppConfig()
  const useAppLink = useAppLinkHandler(ANDROID_APP_PACKAGE_NAME, true)

  useAppLink.open(url, fromDynamicLink)
}

/**
 * 클립보드 복사 기능. IOS도 지원될것임 - 개발버전에선 확인 불가하고 배포해야만 가능
 * @param copiedText 
 * @param copiedMent 
 */
export const clipboard = (copiedText: string, copiedMent?: string) => {
  if (!copiedMent) copiedMent = '클립보드 복사되었습니다'

  if (import.meta.client) { //writeText는 클립보드에 해당 문구를 쓴다는 개념.
    if (process.dev) {
      notify({ type: 'warn', text: '개발버전은 안됩니다' })
    } else {
      navigator?.clipboard?.writeText(copiedText).then(
        () => {
          // notify({ type: 'success', text: copiedText + '\n\n' + copiedMent }) //원랜 이거였는데 대표님이 일단 필요없을것 같다해서 주석
          notify({ type: 'success', text: copiedMent })
        }
      ).catch(err => {
        console.log('Something went wrong', err);
      })
    }
  }
}

export const logToFile = (appName: string, message?: any, ...optionalParams: any[]) => {
  // 배포환경인 경우 파일에 출력
  if (import.meta.server) {
    const toText = (v: any) => {
      try {
        if (v instanceof Error) return v.stack || v.message
        if (typeof v === 'object') return JSON.stringify(v)
        return String(v)
      } catch {
        return String(v)
      }
    }

    // 고해상도 타임스탬프(마이크로초 6자리) 생성
    // performance.timeOrigin + performance.now()를 사용해 epoch 기준 고정밀 시각을 계산
    // 동기 반환을 유지하기 위해 비동기 import 후 fire-and-forget 방식으로 처리
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    import('node:perf_hooks').then(({ performance }) => {
      const epochMs = performance.timeOrigin + performance.now()
      const wholeMs = Math.floor(epochMs)
      const date = new Date(wholeMs)
      const micro = (date.getMilliseconds() * 1000) + Math.floor((epochMs - wholeMs) * 1000)
      const timestamp = `${formatDate(date, 'YYYY-MM-DD HH:mm:ss')}.${String(micro).padStart(6, '0')}`

      const textParts: string[] = [toText(message), ...optionalParams.map(toText)].filter(Boolean)
      const line = `${timestamp}\t${appName}\t${textParts.join(' ')}`

      const filePath = 'E:\\PHP_ERROR\\PHP7_ERROR.log' // 일단 PHP7_ERROR.log를 써서 자주 체크할 수 있게 함. 사용하는 양이 많아지면 NUXT_CONSOLE.log로 변경할 예정
      // const filePath = 'E:\\PHP_ERROR\\NUXT_CONSOLE.log'
      const dirPath = 'E:\\PHP_ERROR'

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      import('node:fs').then((fs) => {
        try {
          if (!fs.existsSync(dirPath)) {
            fs.mkdirSync(dirPath, { recursive: true })
          }
          fs.appendFileSync(filePath, line + "\n", { encoding: 'utf8' })
        } catch {
          // 파일 기록 실패 시 조용히 무시 (서버 로깅에 영향 최소화)
        }
      }).catch(() => { /* ignore */ })
    }).catch(() => { /* ignore */ })

    return
  }

  // 그 외에는 console.log로 출력
  console.log(message, ...optionalParams)
}


/**
 * SSR 공격 등을 막기 위한 공격성 태그 제거 코드. 이걸 하고 v-html에서 사용하면 됨
 * @param text 
 * @returns 
 */
export const domPurify = (text: string) => {
  const purify = DOMPurify() //SSR공격 등 방지 위한 코드
  if (!purify) return text
  return purify.sanitize(text)
}

export const isJsonString = (str?: any) => {
  if (!str || !isString(str)) return false

  try {
    var json = JSON.parse(str);
    return (typeof json === 'object');
  } catch (e) {
    return false;
  }
}

/**
 * 뷰3 라우트쿼리 타입이 LocationQueryValue 이기 때문에 발생하는 타입스크립트 문제 해결을 위한 유틸함수. string|string[] 으로 리턴함
 * @param query 
 * @returns string|string[]
 */
export const parserRouteQuery = (query: LocationQueryValue | LocationQueryValue[]) => {
  if (!query) return undefined

  if (isArray(query)) {
    return query.map((item) => {
      return String(item)
    })
  } else {
    return String(query)
  }
}

/**
 * 어차피 쿼리 오브젝트만 리턴해주므로 eX)getQueryObject(route.fullPath) 이런식으로 써줘도 무방
 * @param path 
 * @returns 
 */
export const getQueryObject = (path: string | URL) => {
  const { fullDomain } = useDYAppConfig()

  if (typeof path === 'string') {
    if (!path.startsWith('/')) { path = '/' + path }

    return Object.fromEntries(new URL(fullDomain + path).searchParams)
  } else {
    return Object.fromEntries(path.searchParams)
  }
}

/**
 * 에러 메시지 기본 핸들링
 * @param error // 에러 객체
 */
export const errorMessageHandler = (error: any) => {
  return error.statusCode === 403 && error.message === '해당 페이지에 대한 권한이 없습니다.(Forbidden)' ? '작성한 내용 중 허용되지 않는 문자열 조합이 있거나, 권한이 없습니다.' : error.message
}

/**
 * SSR 모드일 때 IP를 알아낼 수 있는 메서드. SSR 모드는 node.js 서버가 동작하는 환경이므로 직접 `Request` 객체를 통해 IP를 알아낼 수 있다.
 * @param request 
 * @returns 
 */
export function findIpForSSR(request: any): string | undefined {
  const ip = request.headers['cf-connecting-ip'] ||
    request.headers['x-forwarded-for']?.split(',')?.pop() || // From proxy headers, can be spoofed if you don't have a proxy in front of your app, so drop it if your app is naked.
    request.connection?.remoteAddress ||
    request.socket?.remoteAddress || // socket is an alias to connection, just delete this line
    request.connection?.socket?.remoteAddress

  if (isIpV6(ip)) {
    return ip
  } else if (ip) {
    // IPv6 형태가 아닌 경우에는 포트가 포함되서 전달되는 경우가 있으므로 포트를 제거해서 리턴해야함
    return ip.split(':').pop()
  }

  return undefined
}

export function isIpV4(ip: string) {
  if (!ip || typeof ip !== 'string') return false
  
  // IPv4 정규식 패턴: 4개의 옥텟(0-255)이 점으로 구분
  const ipv4Pattern = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  
  return ipv4Pattern.test(ip.trim())
}

export function isIpV6(ip: string) {
  if (!ip || typeof ip !== 'string') return false
  
  // IPv6 정규식 패턴: 8개의 16비트 그룹이 콜론으로 구분
  const ipv6Pattern = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::1$|^::$|^(?:[0-9a-fA-F]{1,4}:){1,7}:$|^:(?::[0-9a-fA-F]{1,4}){1,7}$|^(?:[0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}$|^[0-9a-fA-F]{1,4}:(?::[0-9a-fA-F]{1,4}){1,6}$|^(?:[0-9a-fA-F]{1,4}:){1,5}(?::[0-9a-fA-F]{1,4}){1,2}$|^(?:[0-9a-fA-F]{1,4}:){1,4}(?::[0-9a-fA-F]{1,4}){1,3}$|^(?:[0-9a-fA-F]{1,4}:){1,3}(?::[0-9a-fA-F]{1,4}){1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,2}(?::[0-9a-fA-F]{1,4}){1,5}$|^[0-9a-fA-F]{1,4}:(?:(?::[0-9a-fA-F]{1,4}){1,6})$|^:(?:(?::[0-9a-fA-F]{1,4}){1,7}|:)$/
  
  return ipv6Pattern.test(ip.trim())
}

/**
 * 주어진 IP가 CIDR 대역에 포함되는지 확인하는 메서드
 * @param ip 
 * @param cidr 
 * @returns 
 */
export function isIpInCidr(ip: string, cidr: string): boolean {
  if (!ip || !cidr) return false
  if (!isIpV4(ip)) return false // 현재 IPv4만 지원

  const [range, bitsStr = '32'] = cidr.split('/')
  const bits = parseInt(bitsStr, 10)
  
  // 32비트 마스크 생성
  const mask = bits === 0 ? 0 : (0xFFFFFFFF << (32 - bits)) >>> 0
  
  const ipInt = ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0
  const rangeInt = range.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0) >>> 0
  
  return (ipInt & mask) === (rangeInt & mask)
}

/**
 * SPA 모드일 때 IP를 알아낼 수 있는 메서드. SPA 모드에서는 서버가 없으므로 실제 IP를 알아낼 수 없기 때문에 별도의 서버에 새로운 요청을 보내서 알아내야 한다.
 */
export async function findIpForSPA(): Promise<string> {
  const { API_BASE_URL } = useDYAppConfig()
  const response = await fetch(API_BASE_URL+'/api/ip')
  return response.text()
}

export function formatDate(date: Date | number | string, format?: string) {
  const time = isNaN(Number(date)) || date instanceof Date ? dayjs(date) : dayjs.unix(Number(date)) //좌항이면 string이라 date형식, 우항이면 타임스탬프
  if (!format) format = 'YYYY-MM-DD'
  return time.format(format)
}

/**
 * 프로토콜을 포함한 도메인 주소를 반환하는 메서드. `https://www.doyac.com` 형태로 반환된다.
 * @param config 
 * @returns 
 */
export function getFullDomain(config: Pick<DYAppConfig, 'protocol' | 'domain'>): string {
  return `${config.protocol}://${config.domain}`
}

/**
 * 2026-07-27 이미지 URL 을 절대 URL 로 만든다. **이미 절대 URL 이면 그대로 둔다.**
 *
 * 왜 필요한가 — upload-migrate 이후 백엔드의 이미지 loadSet(`getImgLazyLoadSet`)은
 * 경로가 `/upFolder/` 로 시작하면 `STORAGE_DOMAIN` 을 붙여 **절대 URL**(`https://storage.doyac.com/...`)로 내려준다.
 * 그런데 프론트는 예전 그대로 `fullDomain + thumbnail_m` 을 해서
 * `https://www.doyac.comhttps://storage.doyac.com/...` 같은 이중 도메인이 만들어졌다.
 * (실제 사례: 정규강의 상세 og:image 표본 6/6 전부 깨져 있었고, 서치콘솔이 악보/MR 의 Product.image 를 반려)
 *
 * ⚠️ `fullUrl`(= fullDomain + basePath, 예 `https://www.doyac.com/v`)을 넘기지 말 것.
 *    이미지 경로는 basePath 아래가 아니므로 `/v/upFolder/...` 가 되어 깨진다. 반드시 `fullDomain` 을 넘긴다.
 *
 * @param src loadSet 의 thumbnail_s/m/l · original 등. 절대 URL 이거나 `/` 로 시작하는 경로.
 * @param fullDomain `https://www.doyac.com` 형태 (useDYAppConfig().fullDomain)
 * @returns 절대 URL. src 가 비어 있으면 빈 문자열(도메인만 남은 쓰레기 URL 방지).
 */
export function absoluteImageUrl(src: string | undefined | null, fullDomain: string): string {
  if (!src) { return '' }
  return /^https?:\/\//i.test(src) ? src : `${fullDomain}${src}`
}

export function numberFormat(value: number) {
  if (value) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  }
  return 0
}

export function join(value: string, glue?: string) {
  if (!Array.isArray(value)) { return }
  if (glue === undefined) { glue = ', ' }
  return value.reduce((prev, current) => `${prev}, ${current}`)
}

/**
 * typedPk를 type, pk로 파싱해주는 함수.
 * @param typedPk
 */
export function parseTypedPk(typedPk: string) {
  if (!typedPk) { return { type: undefined, pk: undefined } }
  let pos = 0
  while (pos < typedPk.length) {
    if (!isNaN(Number(typedPk[pos]))) {
      break
    }
    pos++
  }

  return {
    type: typedPk.substring(0, pos),
    pk: Number(typedPk.substring(pos))
  }
}

/**
 * 보여주고 싶지 않은 부분들 제외하고 실제 컨텐츠만 프린트시키기 위함. hiddenPrintArea 클래스로 보여주지 않을 부분들 설정 필요
 */
export function print() {
  window.onbeforeprint = () => {
    document.querySelectorAll('.hiddenPrintArea').forEach((item) => {
      item.classList.add('!hidden')
    })
  }

  window.onafterprint = () => {
    document.querySelectorAll('.hiddenPrintArea').forEach((item) => {
      item.classList.remove('!hidden')
    })
  }
  try {   ////safari용
    document.execCommand('print', false);
  }
  catch (e) {
    window.print();
  }
}

export function protectedText(originalString: string) {
  const length = originalString.length
  let resultText

  if (length > 4) {
    resultText = originalString.substring(0, 2)
    for (let i = 2; i < length - 2; i++) {
      resultText += '*'
    }

    resultText += originalString.slice(-2)
    if (length > 10) {
      const halfLength = Math.round(length) - 2
      const hiddenArea = originalString.substring(halfLength)
      resultText = resultText.substring(0, halfLength) + hiddenArea + resultText.substring(halfLength + 3, length)
    }
  } else if (length > 3) {
    resultText = originalString.substring(0, 1) + '**' + originalString.substring(length - 1, length)
  } else if (length > 2) {
    resultText = originalString.substring(0, 1) + '*' + originalString.substring(length - 1, length)
  }

  return resultText
}

export function replaceTags(originalString: string) {
  if (!originalString) { return originalString }
  if (originalString) { return originalString.replace(/(&lt;)/gi, '<').replace(/&gt;/gi, '>') }
}

/**
 * getQuerySelector로 가져온 엘리먼트를 넣어주면 됨
 * @param qeurySelector 
 */
export function scrollToElement(qeurySelector: string) {
  nextTick(async () => {
    const elm = document.querySelector(qeurySelector)

    if (elm instanceof HTMLElement) {
      await window.scrollTo({ top: elm?.offsetTop + 100 })
    }
  })
}


/**
 * ref로 지정한 뷰 컴포넌트로 이동
 * @param html // ref로 지정한 뷰 컴포넌트 자체를 넘길것. ex) scrollToRef(AAcomponent.value)
 */
export function scrollToRef(html?: HTMLElement, area?: HTMLElement, topModifier?: number) {
  if (import.meta.client) {
    if (!html) {
      throw new Error(`scrollToRef 에러!! ref 찾을 수 없음 ==>> ${html}`)
    }

    if (!topModifier) topModifier = 0

    if (html instanceof HTMLElement) {
      if (window.IntersectionObserver) {
        if (area) {
          area.scrollTo({ top: window.scrollY + html.getBoundingClientRect().top - 5 + topModifier })
        } else {
          window.scrollTo({ top: window.scrollY + html.getBoundingClientRect().top - 5 + topModifier })
        }

      } else {
        if (area) {
          area.scrollTo(0, window.scrollY + html.getBoundingClientRect().top + 5 + topModifier)
        } else {
          window.scrollTo(0, window.scrollY + html.getBoundingClientRect().top + 5 + topModifier)
        }
      }
    }
  }
}

export function stripTag(originalString: string | undefined) {
  if (originalString === undefined) return ''
  if (!originalString) { return originalString }
  return originalString.replace(/<br\s*\/?>/gi, ' ').replace(/(<([^>]+)>)/gi, '').replace(/&nbsp;/gi, ' ')
}

export function stripEnterLine(originalString: string | undefined) {
  if (originalString === undefined) return ''
  if (!originalString) { return originalString }
  return originalString.replace(/\r\n/g, ' ')
}

export function linkForAdminPageByTableName(tableName: string | undefined) {
  if (tableName === undefined) return ''
  if (tableName === 'dy_lecture_book') { return 'courses' }
  if (tableName === 'book_book') { return 'books' }
  if (tableName === 'shop_book') { return 'shops' }
  if (tableName === 'dy_license_book') { return 'licenses' }
  if (tableName === 'dy_tourguide_book') { return 'tour-guides' }
  if (tableName === 'dy_akbo_book') { return 'akbo-mrs' }
  /**
   * 2026-08-18 (D-72): 입장권 대행. 미등록이라 `''` 를 돌려주고 있어서
   *   ① 결제 관련 메모 링크가 `/payments//21` 로 깨지고
   *   ② 맞춤결제 목록의 연동결제 링크가 어디로도 못 갔다.
   * ⚠️ 이 함수는 3사(닷컴·아트·마퍼가) 공유다. `tg_ticket_book` 은 마퍼가에만 있는 테이블이라
   *    다른 사이트에서는 이 분기에 도달할 데이터 자체가 없다(추가만 하는 변경, 무해).
   */
  if (tableName === 'tg_ticket_book') { return 'tickets' }

  return ''
}

export function getCategoryByTableName(tableName: string | undefined) {
  if (tableName === undefined) return ''
  if (tableName === 'dy_lecture_book') { return '강의' }
  if (tableName === 'book_book') { return '교재' }
  if (tableName === 'shop_book') { return '준비물' }
  if (tableName === 'dy_license_book') { return '자격증' }
  if (tableName === 'dy_tourguide_book') { return '투어' }
  if (tableName === 'dy_akbo_book') { return '악보/MR' }
  // 2026-08-18 (D-72): 위 linkForAdminPageByTableName 과 짝. 둘 중 하나만 등록하면
  //   링크는 가는데 분류명이 비거나 그 반대가 된다.
  if (tableName === 'tg_ticket_book') { return '입장권' }

  return ''
}

export function stripSpecialCharacters(originalString: string) {
  if (!originalString) { return originalString }
  return originalString.replace(/[\{\}\[\]\/?.,;:|\)*~`!^\-_+<>@\#$%&\\\=\(\'\"]/gi, '')
}

/**
 * 유니크 아이디 생성 함수.
 */
export function uniqueId() {
  function s4() {
    return ((1 + Math.random()) * 0x10000 | 0).toString(16).substring(1)
  }
  return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}

/**
 * 주어진 객체에 특정 프로퍼티가 존재하는지 체크하는 함수. 타입스크립트에서 유용하게 쓰임
 * if문에서 쓸 것, bool을 리턴하며, true인 경우에는 해당 프로퍼티로 바로 접근할 수 있게 된다
 * 타입스크립트에서만 알고 있는 인터페이스로 정확한 타입을 체크할때도 유용함
 * @param obj
 * @param prop
 */
export function hasOwnProperty<X extends object, Y extends PropertyKey>(obj: X, prop: Y): obj is X & Record<Y, unknown> {
  return obj.hasOwnProperty(prop)
}

export function getBrowserInfo(userAgent: string) {
  let reg = null
  let result
  const browser = { name: '', version: '' }
  userAgent = userAgent.toLowerCase()
  if (userAgent.includes('opr')) {
    reg = /opr\/(\S+)/g
    browser.name = 'Opera'
    result = reg.exec(userAgent)
    if (result) { browser.version = result[1] }
  } else if (userAgent.includes('edge')) {
    reg = /edge\/(\S+)/g
    browser.name = 'Edge'
    result = reg.exec(userAgent)
    if (result) { browser.version = result[1] }
  } else if (userAgent.includes('chrome')) {
    reg = /chrome\/(\S+)/g
    browser.name = 'Chrome'
    result = reg.exec(userAgent)
    if (result) { browser.version = result[1] }
  } else if (userAgent.includes('safari')) {
    reg = /safari\/(\S+)/g
    browser.name = 'Safari'
    result = reg.exec(userAgent)
    if (result) { browser.version = result[1] }
  } else if (userAgent.includes('firefox')) {
    reg = /firefox\/(\S+)/g
    browser.name = 'Firefox'
    result = reg.exec(userAgent)
    if (result) { browser.version = result[1] }
  } else if (userAgent.includes('trident')) {
    browser.name = 'IE'
    if (userAgent.includes('msie')) {
      reg = /msie (\S+)/g
      result = reg.exec(userAgent)
      if (result) {
        browser.version = result[1]
        browser.version = browser.version.replace(';', '')
      }
    } else {
      reg = /rv:(\S+)/g
      result = reg.exec(userAgent)
      if (result) { browser.version = result[1] }
    }
  }
  return browser
}

export function toHHMMSS(value: string | number) {
  if (value) {
    let sec_num = parseInt(`${value}`, 10); // don't forget the second param
    let hours: string | number = Math.floor(sec_num / 3600);
    let minutes: string | number = Math.floor((sec_num - (hours * 3600)) / 60);
    let seconds: string | number = sec_num - (hours * 3600) - (minutes * 60);

    if (hours < 10) { hours = "0" + hours; }
    if (minutes < 10) { minutes = "0" + minutes; }
    if (seconds < 10) { seconds = "0" + seconds; }
    return hours + ':' + minutes + ':' + seconds;
  }
  return "00:00:00"
}

export function timeInterval(inputdate: string | number) {
  let input = `${inputdate}`
  if (input.length === 10) { input += '000' }

  dayjs.locale('ko')
  dayjs.extend(relativeTime)
  if (typeof inputdate === 'string') {
    if (inputdate.includes('T')) {
      return dayjs(inputdate).fromNow()
    }
  }
  return dayjs(Number(input)).fromNow()
}

export function nl2br(originalString: string | undefined) {
  if (originalString === undefined) return ''
  if (!originalString) { return originalString }
  if (originalString) { return originalString.replace(/\n/g, '<br>') }
}

/**
 * HTML 특수문자를 엔티티로 치환한다.
 *
 * 2026-09-03 추가. 평문을 `v-html` 로 그릴 때 반드시 거쳐야 하는 처리다.
 */
export function escapeHtml(originalString: string | undefined) {
  if (originalString === undefined) return ''
  if (!originalString) { return originalString }
  return originalString
    .replace(/&/g, '&amp;')   // & 를 가장 먼저 바꿔야 뒤에서 만든 엔티티가 다시 안 깨진다
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * 평문(textarea 등으로 입력받은 글)을 `v-html` 로 안전하게 렌더링하기 위한 변환.
 * HTML 이스케이프 후 줄바꿈만 `<br>` 로 바꾼다.
 *
 * 2026-09-03 추가. 기존 {@link nl2br} 은 줄바꿈만 바꾸고 이스케이프를 하지 않아서,
 * 본문에 `<script src="...">` / `<select ...>` / `<오나라>` 같은 문자열이 들어 있으면
 * 브라우저가 그걸 진짜 태그로 파싱해버린다. 특히 닫는 태그가 없는 `<script>` 를 만나면
 * **그 뒤 본문이 통째로 화면에서 사라진다**(전자결재 8122 업무 비고 잘림 사고).
 *
 * 주의: 실제 HTML 마크업이 섞여 저장되는 필드에는 쓰면 안 된다. 태그가 글자로 노출된다.
 * (리치 에디터 본문, 크롤링해 온 <img>, 구시스템이 줄바꿈용으로 넣어둔 <br> 등)
 */
export function nl2brText(originalString: string | undefined) {
  if (originalString === undefined) return ''
  if (!originalString) { return originalString }
  return escapeHtml(originalString)!.replace(/\n/g, '<br>')
}

/**
 * {@link nl2brText} 와 같되, 백엔드 환불 문구가 넣는 **강조 태그 4종만** 진짜 태그로 되살린다.
 *
 * 2026-09-29 추가. 09-03 에 환불정보 화면(refundText)을 nl2brText 로 바꾸면서 "생성 코드에 태그 없음" 으로 판단했으나,
 * 실제로는 TourPaymentRefundManager·ManualPaymentRefundManager 가 `<span style='color:red;'>` / `<b>` 로
 * 환불불가 사유·타사결제 안내를 강조하고 있어 **태그가 글자로 노출**됐다(웹 버그 신고 row 6).
 * refundText 에는 고객이 쓴 환불사유(`contents`)도 섞여 들어오므로 nl2br 로 되돌릴 수는 없다(실제 `<` 포함 2건).
 *
 * 방식: 먼저 전부 이스케이프하고, 아래 목록과 **글자 그대로 같은** 태그만 되돌린다.
 *  - 속성 있는 태그는 `<span style='color:red;'>` 한 형태만 허용한다(속성을 자유롭게 허용하면 onerror 등 주입 경로가 된다).
 *  - 고객이 같은 글자를 적어 넣어도 색·굵기가 바뀔 뿐 스크립트는 들어오지 못한다.
 *  - 새 강조 태그를 백엔드에 쓰려면 여기 목록에도 추가해야 화면에 적용된다.
 */
const EMPHASIS_TAGS_FOR_PLAIN_TEXT: [string, string][] = [
  ["<span style='color:red;'>", '<span style="color:red;">'],
  ['</span>', '</span>'],
  ['<b>', '<b>'],
  ['</b>', '</b>'],
]

export function nl2brTextWithEmphasis(originalString: string | undefined) {
  if (originalString === undefined) return ''
  if (!originalString) { return originalString }
  let html = nl2brText(originalString)!
  for (const [raw, safe] of EMPHASIS_TAGS_FOR_PLAIN_TEXT) {
    html = html.split(escapeHtml(raw)!).join(safe)
  }
  return html
}

/**
 * 기본설정된 값 가져오기 위함
 * 타임스탬프 방식으로 처리하려고 만든 것
 * @param inputdate
 */
export function defaultDayjs(inputdate?: string | number) {
  let input = '' + inputdate
  if (input.length === 10) { input += '000' }

  dayjs.locale('ko')
  dayjs.extend(weekday)

  return inputdate ? dayjs(Number(input)) : dayjs() // 인풋이 아닌 인풋데이트의 여부로 따져야 하는 이유는 ''도 있다고 보는듯..
}

/**
 * 주어진 값이 `null` 또는 `undefined`가 아닌지 검사한다. `Array.filter` 메서드에서 유용하게 쓰인다.
 *
 * @example
 * const array: (string | null)[] = ['foo', 'bar', null, 'zoo', null];
 * const filteredArray: string[] = array.filter(notEmpty); // 리턴되는 타입이 `(string | null)[]` 가 아님에 주목!
 * @param value
 */
export function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}


// [2026-09-04] 이미지 CDN 호스트를 imagedelivery.net → img.doyacstorage.com 으로 교체.
//   imagedelivery.net 은 중국 등 일부 국가/네트워크에서 차단돼 이미지가 아예 안 나온다.
//   두 주소는 같은 Cloudflare Images 계정(계정해시 포함 경로 동일)을 가리키므로 호스트만 바꾸면 된다.
export function getCFImage(imageId: string, options: CloudFlareFlexibleVariants | 'thumbnail' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | DisplayBreakpoint, accountHash: string = 'iF917V18P88uIhA8kbNa1A') {
  const q = (() => {
    if (typeof options === 'string') {
      return options
    } else {
      return Object.entries(options).map(([key, value]) => `${key}=${value}`).join(',')
    }
  })()
  return `https://img.doyacstorage.com/${accountHash}/${imageId}/${q}`
}

export function getCountryName(code: string) {
  const countryNames: Record<string, string> = {
    'AF': '아프가니스탄', 'AL': '알바니아', 'DZ': '알제리', 'AD': '안도라', 'AO': '앙골라',
    'AG': '앤티가 바부다', 'AR': '아르헨티나', 'AM': '아르메니아', 'AU': '호주', 'AT': '오스트리아',
    'AZ': '아제르바이잔', 'BS': '바하마', 'BH': '바레인', 'BD': '방글라데시', 'BB': '바베이도스',
    'BY': '벨라루스', 'BE': '벨기에', 'BZ': '벨리즈', 'BJ': '베냉', 'BT': '부탄',
    'BO': '볼리비아', 'BA': '보스니아 헤르체고비나', 'BW': '보츠와나', 'BR': '브라질', 'BN': '브루나이',
    'BG': '불가리아', 'BF': '부르키나파소', 'BI': '부룬디', 'KH': '캄보디아', 'CM': '카메룬',
    'CA': '캐나다', 'CV': '카보베르데', 'CF': '중앙아프리카공화국', 'TD': '차드', 'CL': '칠레',
    'CN': '중국', 'CO': '콜롬비아', 'KM': '코모로', 'CG': '콩고', 'CR': '코스타리카',
    'HR': '크로아티아', 'CU': '쿠바', 'CY': '키프로스', 'CZ': '체코', 'DK': '덴마크',
    'DJ': '지부티', 'DM': '도미니카', 'DO': '도미니카공화국', 'EC': '에콰도르', 'EG': '이집트',
    'SV': '엘살바도르', 'GQ': '적도기니', 'ER': '에리트레아', 'EE': '에스토니아', 'ET': '에티오피아',
    'FJ': '피지', 'FI': '핀란드', 'FR': '프랑스', 'GA': '가봉', 'GM': '감비아',
    'GE': '조지아', 'DE': '독일', 'GH': '가나', 'GR': '그리스', 'GD': '그레나다',
    'GT': '과테말라', 'GN': '기니', 'GW': '기니비사우', 'GY': '가이아나', 'HT': '아이티',
    'HN': '온두라스', 'HK': '홍콩', 'HU': '헝가리', 'IS': '아이슬란드', 'IN': '인도',
    'ID': '인도네시아', 'IR': '이란', 'IQ': '이라크', 'IE': '아일랜드', 'IL': '이스라엘',
    'IT': '이탈리아', 'JM': '자메이카', 'JP': '일본', 'JO': '요르단', 'KZ': '카자흐스탄',
    'KE': '케냐', 'KI': '키리바시', 'KP': '북한', 'KR': '대한민국', 'KW': '쿠웨이트',
    'KG': '키르기스스탄', 'LA': '라오스', 'LV': '라트비아', 'LB': '레바논', 'LS': '레소토',
    'LR': '라이베리아', 'LY': '리비아', 'LI': '리히텐슈타인', 'LT': '리투아니아', 'LU': '룩셈부르크',
    'MO': '마카오', 'MK': '북마케도니아', 'MG': '마다가스카르', 'MW': '말라위', 'MY': '말레이시아',
    'MV': '몰디브', 'ML': '말리', 'MT': '몰타', 'MH': '마셜 제도', 'MR': '모리타니',
    'MU': '모리셔스', 'MX': '멕시코', 'FM': '미크로네시아', 'MD': '몰도바', 'MC': '모나코',
    'MN': '몽골', 'ME': '몬테네그로', 'MA': '모로코', 'MZ': '모잠비크', 'MM': '미얀마',
    'NA': '나미비아', 'NR': '나우루', 'NP': '네팔', 'NL': '네덜란드', 'NZ': '뉴질랜드',
    'NI': '니카라과', 'NE': '니제르', 'NG': '나이지리아', 'NO': '노르웨이', 'OM': '오만',
    'PK': '파키스탄', 'PW': '팔라우', 'PS': '팔레스타인', 'PA': '파나마', 'PG': '파푸아뉴기니',
    'PY': '파라과이', 'PE': '페루', 'PH': '필리핀', 'PL': '폴란드', 'PT': '포르투갈',
    'QA': '카타르', 'RO': '루마니아', 'RU': '러시아', 'RW': '르완다', 'KN': '세인트키츠네비스',
    'LC': '세인트루시아', 'VC': '세인트빈센트그레나딘', 'WS': '사모아', 'SM': '산마리노', 'ST': '상투메프린시페',
    'SA': '사우디아라비아', 'SN': '세네갈', 'RS': '세르비아', 'SC': '세이셸', 'SL': '시에라리온',
    'SG': '싱가포르', 'SK': '슬로바키아', 'SI': '슬로베니아', 'SB': '솔로몬 제도', 'SO': '소말리아',
    'ZA': '남아프리카공화국', 'SS': '남수단', 'ES': '스페인', 'LK': '스리랑카', 'SD': '수단',
    'SR': '수리남', 'SZ': '에스와티니', 'SE': '스웨덴', 'CH': '스위스', 'SY': '시리아',
    'TW': '대만', 'TJ': '타지키스탄', 'TZ': '탄자니아', 'TH': '태국', 'TL': '동티모르',
    'TG': '토고', 'TO': '통가', 'TT': '트리니다드토바고', 'TN': '튀니지', 'TR': '터키',
    'TM': '투르크메니스탄', 'TV': '투발루', 'UG': '우간다', 'UA': '우크라이나', 'AE': '아랍에미리트',
    'GB': '영국', 'US': '미국', 'UY': '우루과이', 'UZ': '우즈베키스탄', 'VU': '바누아투',
    'VA': '바티칸', 'VE': '베네수엘라', 'VN': '베트남', 'YE': '예멘', 'ZM': '잠비아',
    'ZW': '짐바브웨'
  }
  return countryNames[code] || code
}

/**
 * 특정영역만 잡아서 프린트하기 위한 함수. 지정한 id이하의 내용만 출력하며 css호환을 위해 아래 내용은 기본적으로 가져가는게 좋음
 <div :id="`printContents${item.no}`">
  <div class="v-application">
    <div class="vuetify text-center">
      들어가는 내용들
    </div>
  </div>
 </div>
 * @param targetId
 */
export function printSpecificArea(targetId: string) {
  const html = document.querySelector('html')
  const printContents = '' + document.querySelector('#' + targetId)?.innerHTML
  const printDiv = document.createElement('div')
  printDiv.className = 'print-div'

  html?.appendChild(printDiv)
  printDiv.innerHTML = printContents
  document.body.style.display = 'none'

  window.onbeforeprint = function () {
    document.body.style.display = 'none'
  }
  window.onafterprint = function () {
    document.body.style.display = 'block'
    printDiv.style.display = 'none'
  }

  try {
    document.execCommand('print', false, undefined)
  }
  catch (e) {
    window.print()
  }
  document.body.style.display = 'block'
  printDiv.style.display = 'none'
}

export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // 해외번호 처리 (+로 시작하는 경우)
  if (phone.startsWith('+')) {
    // 이미 포맷팅된 형식이면 그대로 반환 (공백이나 하이픈이 있는 경우)
    if (/[\s\-]/.test(phone)) {
      return phone;
    }
    // 포맷팅되지 않은 경우 원본 반환 (추후 필요시 포맷팅 로직 추가 가능)
    return phone;
  }
  
  // 공백과 하이픈 제거 후 숫자만 추출
  const cleaned = phone.replace(/[\s\-]/g, '');
  
  // 숫자가 아닌 문자 제거
  const numbers = cleaned.replace(/\D/g, '');
  
  // 길이에 따라 포맷팅
  if (numbers.length === 10) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
  } else if (numbers.length === 11) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
  } else if (numbers.length === 8) {
    return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
  }
  
  // 기본 포맷이 맞지 않으면 원본 반환
  return phone;
};

export async function tryReadJson(path: string) {
  if (import.meta.server) {
    try {
      const fs = await import('node:fs')
      if (fs.existsSync(path)) {
        const content = fs.readFileSync(path, 'utf-8')
        return JSON.parse(content)
      }
    } catch (e) {
      console.error(`[prepare-store.server] Failed to read or parse JSON at ${path}:`, e)
    }
  }

  return undefined
}

export function cleanDoubleSlashes(url: string | null | undefined): string {
  if (!url) return ''
  // http:// or https:// 뒤의 중복된 /를 하나로 합침
  // (?<!:) lookbehind는 구버전 Safari(16.3 이하)에서 지원하지 않으므로 캡처 그룹 방식으로 대체
  return url.replace(/(^|[^:])\/\//g, '$1/')
}

/**
 * 260922 추가. 여러 비동기 작업을 **동시 실행 수를 제한해서** 실행한다.
 * 관리자 목록의 일괄삭제·일괄수정처럼 "선택한 N건을 단건 API 로 처리해야 하는" 경우에 쓴다.
 *
 * - 한 번에 다 던지면(`Promise.all(items.map(...))`) 30건 선택 시 동시요청 30개가 나가 서버(PHP 워커)를
 *   한 사용자가 과점하고, 브라우저의 도메인당 연결 한도(HTTP/1.1 기준 6개)에 걸려 어차피 큐잉된다.
 * - 반대로 한 건씩 순차 처리하면 30건에 수십 초가 걸린다.
 * - 그래서 `size` 개씩 묶어 **배치 단위로 동시 실행 → 그 배치가 끝나면 다음 배치**로 간다.
 *
 * `Promise.allSettled` 를 쓰므로 **중간에 실패한 건이 있어도 나머지는 끝까지 진행**하고,
 * 성공/실패를 나눠서 돌려준다. 호출부는 마지막에 "성공 N건 / 실패 M건" 을 한 번만 알리면 된다.
 *
 * @param items 처리할 대상 목록 (예: 선택된 주키 배열)
 * @param worker 대상 1건을 처리하는 비동기 함수 (예: `(no) => remove(no)`)
 * @param options.size 동시 실행 수. 기본 5 (아래 주석 참고)
 * @param options.onProgress 배치가 끝날 때마다 호출. 진행률 표시용
 *
 * @example
 * const { succeeded, failed } = await runInBatches(checkedNos.value, (no) => remove(no), {
 *   onProgress: (done, total) => { progressText.value = `${done}/${total}` }
 * })
 * if (failed.length > 0) notify({ type: 'error', text: `${succeeded.length}건 삭제, ${failed.length}건 실패` })
 */
export async function runInBatches<T, R>(
  items: T[],
  worker: (item: T, index: number) => Promise<R>,
  options?: {
    /**
     * 동시 실행 수. 기본값 5.
     * 5 로 잡은 근거 — ① HTTP/1.1 로 떨어져도 브라우저의 도메인당 동시연결 한도(6) 안에 들어가고,
     * ② 관리자 한 명이 서버 워커를 과점하지 않으며, ③ 30건 기준 6배치라 순차 대비 체감이 충분히 빠르다.
     * 처리 1건이 눈에 띄게 무거운 API(대용량 파일 삭제, 외부 결제사 연동 등)라면 2~3 으로 낮출 것.
     */
    size?: number
    onProgress?: (doneCount: number, totalCount: number) => void
  }
): Promise<{
  /** 성공한 작업의 반환값들. 실패분은 빠지므로 items 와 인덱스가 일치하지 않는다 */
  results: R[]
  /** 성공한 대상들 */
  succeeded: T[]
  /** 실패한 대상과 그 에러 */
  failed: { item: T, error: any }[]
}> {
  const size = Math.max(1, options?.size ?? 5)
  const results: R[] = []
  const succeeded: T[] = []
  const failed: { item: T, error: any }[] = []

  for (let start = 0; start < items.length; start += size) {
    const batch = items.slice(start, start + size)
    const settled = await Promise.allSettled(batch.map((item, i) => worker(item, start + i)))

    settled.forEach((result, i) => {
      const item = batch[i]
      if (result.status === 'fulfilled') {
        succeeded.push(item)
        results.push(result.value)
      } else {
        failed.push({ item, error: result.reason })
      }
    })

    options?.onProgress?.(Math.min(start + size, items.length), items.length)
  }

  return { results, succeeded, failed }
}
